import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Grid, Divider, Stepper, Step, StepLabel, Chip } from "@mui/material";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import StatusChip from "../components/StatusChip";
import { useDispatch, useSelector } from "react-redux";
import { fetchTenantById, suspendTenant, reactivateTenant, clearCurrentTenant } from "store/slices/tenantSlice";

const LIFECYCLE_STATES = ["provisioning", "active", "suspended", "maintenance", "offboarding", "archived"];

const VALID_TRANSITIONS = {
  provisioning: ["active"],
  active: ["suspended", "maintenance", "offboarding"],
  suspended: ["active", "offboarding"],
  maintenance: ["active"],
  offboarding: ["archived"],
  archived: [],
};

function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { current: tenant, detailLoading, detailError, actionLoading } = useSelector(
    (state) => state.tenants
  );

  useEffect(() => {
    if (id) dispatch(fetchTenantById(id));
    return () => dispatch(clearCurrentTenant());
  }, [dispatch, id]);

  const currentStateIndex = tenant
    ? LIFECYCLE_STATES.indexOf(tenant.status || tenant.lifecycle?.currentState)
    : 0;

  const validActions = tenant ? VALID_TRANSITIONS[tenant.status] || [] : [];

  const handleSuspend = () => {
    if (window.confirm("Are you sure you want to suspend this tenant?")) {
      dispatch(suspendTenant({ id, reason: "Admin action" }));
    }
  };

  const handleReactivate = () => {
    dispatch(reactivateTenant(id));
  };

  if (detailLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center">
          <MDTypography variant="h6" color="text">Loading tenant details...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (detailError || !tenant) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} textAlign="center">
          <MDTypography variant="h6" color="error">
            {detailError || "Tenant not found"}
          </MDTypography>
          <MDButton color="info" onClick={() => navigate("/tenants")} sx={{ mt: 2 }}>
            Back to Tenants
          </MDButton>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Back button */}
        <MDBox mb={2}>
          <MDButton variant="text" color="info" onClick={() => navigate("/tenants")}>
            <Icon>arrow_back</Icon>&nbsp;Back to Tenants
          </MDButton>
        </MDBox>

        {/* Header Card */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
              <MDBox>
                <MDTypography variant="h4" fontWeight="medium">
                  {tenant.name}
                </MDTypography>
                <MDTypography variant="body2" color="text" mt={0.5}>
                  Tenant ID: {tenant.tenantId} &nbsp;|&nbsp; Plan:{" "}
                  <strong>{(tenant.plan || "").charAt(0).toUpperCase() + (tenant.plan || "").slice(1)}</strong>
                </MDTypography>
              </MDBox>
              <MDBox display="flex" gap={1} alignItems="center">
                <StatusChip status={tenant.status} />
                {validActions.includes("suspended") && (
                  <MDButton
                    color="warning"
                    size="small"
                    onClick={handleSuspend}
                    disabled={actionLoading}
                  >
                    <Icon>pause_circle</Icon>&nbsp;Suspend
                  </MDButton>
                )}
                {validActions.includes("active") && tenant.status === "suspended" && (
                  <MDButton
                    color="success"
                    size="small"
                    onClick={handleReactivate}
                    disabled={actionLoading}
                  >
                    <Icon>play_circle</Icon>&nbsp;Reactivate
                  </MDButton>
                )}
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>

        {/* Lifecycle State Machine */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Lifecycle State
            </MDTypography>
            <Stepper activeStep={currentStateIndex} alternativeLabel>
              {LIFECYCLE_STATES.map((state) => (
                <Step key={state}>
                  <StepLabel>
                    {state.charAt(0).toUpperCase() + state.slice(1)}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </MDBox>
        </Card>

        {/* Details Grid */}
        <Grid container spacing={3}>
          {/* Contact Info */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Contact Information
                </MDTypography>
                <Divider />
                <MDBox mt={2}>
                  <InfoRow label="Contact Name" value={tenant.contact?.name || "—"} />
                  <InfoRow label="Email" value={tenant.contact?.email || tenant.contactEmail || "—"} />
                  <InfoRow label="Phone" value={tenant.contact?.phone || "—"} />
                  <InfoRow label="Country" value={tenant.legalEntity?.country || "—"} />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Billing Info */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Billing Information
                </MDTypography>
                <Divider />
                <MDBox mt={2}>
                  <InfoRow label="Billing Contact" value={tenant.billing?.contactName || "—"} />
                  <InfoRow label="Billing Email" value={tenant.billing?.contactEmail || "—"} />
                  <InfoRow label="Billing Cycle" value={tenant.billing?.duration || "—"} />
                  <InfoRow label="Address" value={tenant.billing?.contactAddress || "—"} />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Metadata */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Metadata
                </MDTypography>
                <Divider />
                <MDBox mt={2}>
                  <InfoRow label="Product Type" value={tenant.metadata?.productType || "—"} />
                  <InfoRow
                    label="Created"
                    value={tenant.metadata?.createdAt ? new Date(tenant.metadata.createdAt).toLocaleDateString() : "—"}
                  />
                  <InfoRow
                    label="Last Modified"
                    value={tenant.metadata?.lastModified ? new Date(tenant.metadata.lastModified).toLocaleDateString() : "—"}
                  />
                  <InfoRow label="Created By" value={tenant.metadata?.createdBy || "—"} />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* State History */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  State History
                </MDTypography>
                <Divider />
                <MDBox mt={2}>
                  {(tenant.lifecycle?.stateHistory || []).map((entry, idx) => (
                    <MDBox key={idx} display="flex" justifyContent="space-between" mb={1} pb={1}
                      sx={{ borderBottom: idx < tenant.lifecycle.stateHistory.length - 1 ? "1px solid #f0f0f0" : "none" }}
                    >
                      <MDBox>
                        <MDTypography variant="caption" fontWeight="medium">
                          {entry.fromState || "—"} → {entry.toState}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          {entry.reason}
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="caption" color="secondary">
                        {new Date(entry.timestamp).toLocaleDateString()}
                      </MDTypography>
                    </MDBox>
                  ))}
                  {(!tenant.lifecycle?.stateHistory || tenant.lifecycle.stateHistory.length === 0) && (
                    <MDTypography variant="caption" color="text">No state transitions recorded</MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

// Helper component for info rows
function InfoRow({ label, value }) {
  return (
    <MDBox display="flex" justifyContent="space-between" mb={1.5}>
      <MDTypography variant="caption" fontWeight="medium" color="text">
        {label}:
      </MDTypography>
      <MDTypography variant="caption" color="dark">
        {value}
      </MDTypography>
    </MDBox>
  );
}

export default TenantDetail;
